/**
 * /whatsapp/inbox — Lista de conversas (esquerda) + Chat (direita).
 *
 * Split-view com polling 10s na lista de conversas. Persistencia da
 * conversa selecionada em localStorage (sobrevive reload).
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiInbox, FiRefreshCw, FiSearch } from "react-icons/fi";

import EntityHeader from "../../../components/EntityHeader";
import { useAuth } from "../../../context/AuthContext";
import useWhatsappPolling from "../../../hooks/useWhatsappPolling";
import { listarConversas } from "../../../services/whatsapp";
import { PageContainer } from "../../../styles/shared";
import {
  EmptyMsg,
  ErrorMsg,
  Pill,
  Select,
} from "../../Orcamentos/styles";
import {
  Avatar,
  ChatEmpty,
  ConversaInfo,
  ConversaItemRow,
  ConversaMeta,
  ConversasFiltros,
  ConversasHeader,
  ConversasPane,
  ConversasScroll,
  InboxLayout,
} from "../styles";
import Chat from "./Chat";
import { getItemTenant, setItemTenant } from "../../../utils/tenantStorage";

const LS_KEY_CONVERSA_ATIVA = "pharuerp:whatsapp:conversa-ativa";

function avatarLetras(nome, wa_number) {
  const fonte = (nome || wa_number || "?").trim();
  const partes = fonte.split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  if (partes.length === 1) return partes[0].slice(0, 2).toUpperCase();
  return (partes[0][0] + partes[partes.length - 1][0]).toUpperCase();
}

function timeRelativo(iso) {
  if (!iso) return "";
  try {
    const date = new Date(iso);
    const diff = Date.now() - date.getTime();
    const min = Math.floor(diff / 60000);
    if (min < 1) return "agora";
    if (min < 60) return `${min}m`;
    const h = Math.floor(min / 60);
    if (h < 24) return `${h}h`;
    const d = Math.floor(h / 24);
    if (d < 7) return `${d}d`;
    return date.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" });
  } catch {
    return "";
  }
}

export default function InboxLista() {
  const navigate = useNavigate();
  const { conversaId: conversaIdUrl } = useParams();
  const { request, hasPermission } = useAuth();

  const podeAtender = hasPermission("whatsapp:atender");

  const [conversas, setConversas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [filtroStatus, setFiltroStatus] = useState("ABERTA");
  const [busca, setBusca] = useState("");

  const [conversaAtivaId, setConversaAtivaId] = useState(() => {
    if (conversaIdUrl) return conversaIdUrl;
    try {
      return getItemTenant(LS_KEY_CONVERSA_ATIVA) || null;
    } catch {
      return null;
    }
  });

  const carregar = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await listarConversas(request, {
        status: filtroStatus || undefined,
        busca: busca || undefined,
        pageSize: 200,
      });
      const lista = Array.isArray(payload) ? payload : payload?.results || [];
      setConversas(lista);
    } catch (err) {
      setError(err?.message || "Erro ao carregar conversas.");
    } finally {
      setLoading(false);
    }
  }, [request, filtroStatus, busca]);

  useWhatsappPolling(carregar, 10000, [carregar]);

  // Sincroniza URL com state local
  useEffect(() => {
    if (conversaIdUrl && conversaIdUrl !== conversaAtivaId) {
      setConversaAtivaId(conversaIdUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversaIdUrl]);

  const handleSelecionar = (id) => {
    setConversaAtivaId(id);
    try {
      setItemTenant(LS_KEY_CONVERSA_ATIVA, id);
    } catch (_e) {
      // ignore
    }
    navigate(`/whatsapp/inbox/${id}`, { replace: true });
  };

  const conversaAtiva = useMemo(
    () => conversas.find((c) => c.id === conversaAtivaId) || null,
    [conversas, conversaAtivaId],
  );

  if (!podeAtender) {
    return (
      <PageContainer>
        <ErrorMsg>Você não tem permissão para atender no WhatsApp.</ErrorMsg>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <EntityHeader
        title="Inbox WhatsApp"
        subtitle={`${conversas.length} conversas carregadas`}
        secondaryActions={[
          {
            label: "Atualizar",
            icon: <FiRefreshCw />,
            onClick: carregar,
          },
        ]}
      />

      <InboxLayout>
        <ConversasPane>
          <ConversasHeader>
            <ConversasFiltros>
              <div style={{ flex: 1, position: "relative" }}>
                <FiSearch
                  style={{
                    position: "absolute",
                    left: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    color: "#94a3b8",
                  }}
                />
                <input
                  type="search"
                  placeholder="Buscar nome ou número..."
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "6px 8px 6px 28px",
                    border: "1px solid #cbd5e1",
                    borderRadius: 6,
                    fontSize: 13,
                  }}
                />
              </div>
              <Select
                value={filtroStatus}
                onChange={(e) => setFiltroStatus(e.target.value)}
                style={{ maxWidth: 130 }}
              >
                <option value="ABERTA">Abertas</option>
                <option value="FECHADA">Fechadas</option>
                <option value="ARQUIVADA">Arquivadas</option>
                <option value="">Todas</option>
              </Select>
            </ConversasFiltros>
          </ConversasHeader>

          <ConversasScroll>
            {error && <ErrorMsg style={{ margin: 12 }}>{error}</ErrorMsg>}
            {!error && conversas.length === 0 && !loading && (
              <EmptyMsg>
                <FiInbox size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
                <div>Nenhuma conversa ainda.</div>
                <div style={{ fontSize: 12 }}>
                  Receba uma mensagem do cliente para começar.
                </div>
              </EmptyMsg>
            )}

            {conversas.map((c) => {
              const nomeExibido = c.contact_nome || c.contact_wa_number || "—";
              const preview =
                c.ultima_mensagem_preview ||
                c.preview ||
                (c.ultima_mensagem_em ? "(sem preview)" : "(sem mensagens)");
              const naoLidas = Number(c.nao_lidas_count || 0);
              return (
                <ConversaItemRow
                  key={c.id}
                  $ativo={c.id === conversaAtivaId}
                  onClick={() => handleSelecionar(c.id)}
                >
                  <Avatar>{avatarLetras(c.contact_nome, c.contact_wa_number)}</Avatar>
                  <ConversaInfo>
                    <span className="nome">{nomeExibido}</span>
                    <span className="preview">{preview}</span>
                  </ConversaInfo>
                  <ConversaMeta>
                    <span className="quando">
                      {timeRelativo(c.ultima_mensagem_em || c.updated_at)}
                    </span>
                    {naoLidas > 0 && (
                      <Pill $tone="warning">{naoLidas > 99 ? "99+" : naoLidas}</Pill>
                    )}
                  </ConversaMeta>
                </ConversaItemRow>
              );
            })}
          </ConversasScroll>
        </ConversasPane>

        {conversaAtivaId ? (
          <Chat
            conversaId={conversaAtivaId}
            conversaResumo={conversaAtiva}
            onAtualizarLista={carregar}
          />
        ) : (
          <ChatPaneVazio />
        )}
      </InboxLayout>
    </PageContainer>
  );
}

function ChatPaneVazio() {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "#f9fafb",
        border: "1px solid #e5e7eb",
        borderRadius: 12,
        minHeight: 400,
      }}
    >
      <ChatEmpty>
        <FiInbox size={48} style={{ opacity: 0.3 }} />
        <div>Selecione uma conversa para visualizar.</div>
      </ChatEmpty>
    </div>
  );
}
